import React, { Suspense, lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, useOverlay } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import { useGroupLimits } from '../../providers/GroupLimitsProvider'

const CreateGroupOverlay = lazy(() => import('../default-imports/CreateGroupOverlay'))

/**
 * Button that handles everything around creating a new group.
 * @param {object} props - The props
 * @param {number | undefined} props.groupsCount - Count of user's groups.
 * @param {Function} props.onCreateGroup - A callback function that is called as GroupList successfully reloads the list as a result of creating a new group. Parameter: (groupId)
 * @returns {JSX.Element} - The JSX
 */
export default function CreateGroupButton({ groupsCount, onCreateGroup }) {
  const [t] = useTranslation()
  const createGroupOverlay = useOverlay()
  const { maxGroupsPerUser, loading: loadingGroupLimits } = useGroupLimits()

  const loading = loadingGroupLimits || !Number.isInteger(groupsCount)
  // Check length of groups to disable button when group limit has been reached
  const overLimit =
    Number.isInteger(maxGroupsPerUser) && Number.isInteger(groupsCount) ? groupsCount >= maxGroupsPerUser : undefined

  return (
    <>
      <Button
        data-testid="create-group-button.create-group.button"
        disabled={loading || overLimit}
        Icon={ContentAdd}
        onClick={createGroupOverlay.handleClick}
        linkName="Create Group Button"
        emphasis="medium"
        title={
          overLimit ? t('group-management.create-group-max-groups.error', { maxGroups: maxGroupsPerUser }) : undefined
        }
      >
        {t('group-management.create-group.action')}
      </Button>
      {!overLimit && !loadingGroupLimits && (createGroupOverlay.isOpen || createGroupOverlay.transitioning) && (
        <Suspense fallback="">
          <CreateGroupOverlay overlay={createGroupOverlay} onCreateGroup={onCreateGroup} />
        </Suspense>
      )}
    </>
  )
}
